import { ICON_COLOR } from '@zep-us/zus/tokens';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { logger } from '@zep/core-logger';
import { LoadingImage } from '@/components/Loading';
import { useEffectOnce } from '@/hooks/useEffectOnce';
import { ChevronLeftIcon, GlobeIcon, LoginIcon } from '@/icons';
import { MyMenuDropdown } from '@/layout/Header/MyMenuDropdown';
import { useUser } from '@/lib/auth';
import { DelayedLoadingFallback } from '@/shared/components/DelayedLoadingFallback';
import { LanguageSelector } from '@/shared/components/LanguageSelector';
import { TemplatePicker } from '@/shared/features/board/components/TemplatePicker';
import { Button, IconButton } from '@/ui_v3';
import { useCreateBoardFromTemplate } from './hooks/useCreateBoardFromTemplate';
export function NewBoardPageView() {
  const router = useRouter();
  const {
    t
  } = useTranslation('board');
  const {
    user,
    isInitialized
  } = useUser();
  const {
    createBlankBoard,
    createFromTemplate,
    isCreating
  } = useCreateBoardFromTemplate();
  useEffectOnce(() => {
    logger.track('quiz_board_creation_page_view');
  });
  return <main className="relative min-h-screen bg-neutral" aria-busy={isCreating} data-sentry-component="NewBoardPageView" data-sentry-source-file="NewBoardPageView.tsx">
      <header className="flex h-[60px] items-center justify-between border-b border-alternative bg-white px-md tablet-sm:h-[64px] tablet-sm:px-[40px]">
        <div className="flex min-w-0 items-center gap-md">
          <IconButton size="md" variant="outlined" intent="neutral" onClick={() => router.back()} data-sentry-element="IconButton" data-sentry-source-file="NewBoardPageView.tsx">
            <ChevronLeftIcon size="md" data-sentry-element="ChevronLeftIcon" data-sentry-source-file="NewBoardPageView.tsx" />
          </IconButton>
          <span className="flex min-w-0 items-center gap-xs">
            <h1 className="truncate text-body-lg-bold text-default">
              {t('board.new.title')}
            </h1>
            <span className="rounded-xs border border-opacity-black-2 bg-neutral px-2xs py-4xs text-caption-md-semibold text-neutral">
              Beta
            </span>
          </span>
        </div>
        {isInitialized && (user ? <MyMenuDropdown hideBadge /> : <GuestHeaderActions />)}
      </header>

      <div className="mx-auto flex w-full max-w-[900px] flex-col px-md py-2xl tablet-sm:px-0">
        <TemplatePicker onCreateBlank={createBlankBoard} onSelect={createFromTemplate} disabled={isCreating} data-sentry-element="TemplatePicker" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>

      {isCreating && <DelayedLoadingFallback delayMS={500}>
          <div role="status" className="absolute inset-0 z-modal flex items-center justify-center bg-dimmed">
            <LoadingImage className="size-[64px] brightness-0 invert" />
          </div>
        </DelayedLoadingFallback>}
    </main>;
}
const GuestHeaderActions = () => {
  const router = useRouter();
  const {
    t
  } = useTranslation('board');
  const loginHref = {
    pathname: '/login',
    query: {
      callbackUrl: router.asPath
    }
  };

  // 보드 생성 화면은 아직 board_id가 없어 location만 남긴다(header/modal과 구분).
  const handleLoginClick = () => {
    logger.track('board_login_click', {
      location: 'creation_header'
    });
  };
  return <div className="flex shrink-0 items-center gap-xs" data-sentry-component="GuestHeaderActions" data-sentry-source-file="NewBoardPageView.tsx">
      <Link href={loginHref} className="tablet-sm:hidden" onClick={handleLoginClick} data-sentry-element="Link" data-sentry-source-file="NewBoardPageView.tsx">
        <IconButton variant="transparent" intent="neutral" size="md" data-sentry-element="IconButton" data-sentry-source-file="NewBoardPageView.tsx">
          <LoginIcon size="md" fill={ICON_COLOR.neutral} data-sentry-element="LoginIcon" data-sentry-source-file="NewBoardPageView.tsx" />
        </IconButton>
      </Link>
      <Link href={loginHref} className="hidden tablet-sm:block" onClick={handleLoginClick} data-sentry-element="Link" data-sentry-source-file="NewBoardPageView.tsx">
        <Button variant="transparent" intent="neutral" size="md" className="rounded-full" data-sentry-element="Button" data-sentry-source-file="NewBoardPageView.tsx">
          {t('board.header.login')}
        </Button>
      </Link>
      <div className="tablet-sm:hidden">
        <LanguageSelector size="md" variant="transparent" intent="neutral" eventProps={{
        location: 'board_new_header'
      }} trigger={<IconButton variant="transparent" intent="neutral" size="md">
              <GlobeIcon size="md" fill={ICON_COLOR.neutral} />
            </IconButton>} data-sentry-element="LanguageSelector" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>
      <div className="hidden tablet-sm:flex">
        <LanguageSelector size="md" variant="transparent" intent="neutral" rounded="full" eventProps={{
        location: 'board_new_header'
      }} data-sentry-element="LanguageSelector" data-sentry-source-file="NewBoardPageView.tsx" />
      </div>
    </div>;
};